/* eslint-disable */
// /src/pages/TaxCenterPage.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchMyTaxSummaryOptimized } from "../services/taxService";
import PageTopBar from "../components/PageTopBar";
import { useDriverStatus } from "hooks/useDriverStatus";
import { useDriverStatusContext } from "context/DriverStatusContext";

// 연도 범위
const YEAR_MIN = 2024;
const YEAR_MAX = 2025;

const formatKRW = (v) => `${Number(v || 0).toLocaleString("ko-KR")}원`;

// 1~12월 기본 버킷 (초기 로딩용)
function makeEmptyMonthRows() {
  return Array.from({ length: 12 }, (_, idx) => ({
    month: idx + 1,
    totalFare: 0,
    fee: 0,
    service: 0,
  }));
}

export default function TaxCenterPage() {
  const [year, setYear] = useState(2025);

  const [monthRows, setMonthRows] = useState(makeEmptyMonthRows);
  const [annual, setAnnual] = useState({
    totalFare: 0,
    fee: 0,
    service: 0,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // 🔹 드라이버 상태 훅에서 가맹점 이름 / 등급 등 가져오기
  const {
    franchisename, // settlements_raw."가맹점(운수사명)"와 매핑
    grade,
    membershipBarcode,
    name: dsName,
    phoneE164: dsPhoneE164,
  } = useDriverStatusContext();

  const franchiseName = franchisename || "";

  const handlePrevYear = () => {
    if (year <= YEAR_MIN) return;
    setYear((y) => Math.max(YEAR_MIN, y - 1));
  };

  const handleNextYear = () => {
    if (year >= YEAR_MAX) return; // 내년으로는 못 가게
    setYear((y) => Math.min(YEAR_MAX, y + 1));
  };

  // 🔹 Firestore에서 실제 세무자료 불러오기
  useEffect(() => {
    let alive = true;

    async function run() {
      setErr("");

      if (!franchiseName) {
        console.warn("[TaxCenterPage] franchisename 없음 – 빈 데이터로 표시", {
          franchisename,
          grade,
          dsName,
          dsPhoneE164,
        });
        setMonthRows(makeEmptyMonthRows());
        setAnnual({ totalFare: 0, fee: 0, service: 0 });
        return;
      }

      try {
        setLoading(true);
        const { monthRows, annual } = await fetchMyTaxSummaryOptimized({
          year,
          franchiseName: franchisename,
        });
        if (!alive) return;
        setMonthRows(monthRows);
        setAnnual(annual);
      } catch (e) {
        console.error("[TaxCenterPage] load error", e);
        if (!alive) return;
        setErr("세무자료를 불러오는 중 오류가 발생했습니다.");
        setMonthRows(makeEmptyMonthRows());
        setAnnual({ totalFare: 0, fee: 0, service: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();

    return () => {
      alive = false;
    };
  }, [year, franchiseName, grade, dsName, dsPhoneE164]);

  return (
    <BG>
      <PageTopBar variant="back" label="뒤로" />

      <MainWrap>
        <TitleBlock>
          <BrandRow>
            <BrandKakao>kakaoT</BrandKakao>
            <BrandName>Hey’ Way</BrandName>
          </BrandRow>
        </TitleBlock>

        <SubTitle>세무자료 센터</SubTitle>

        {/* 연도 선택 */}
        <YearRow>
          <YearArrow
            type="button"
            onClick={handlePrevYear}
            disabled={year <= YEAR_MIN}
          >
            ‹
          </YearArrow>
          <YearText>{year}년</YearText>
          <YearArrow
            type="button"
            onClick={handleNextYear}
            disabled={year >= YEAR_MAX}
          >
            ›
          </YearArrow>
        </YearRow>

        <SummaryCard>
          <SummaryTitle>{year}년 운행·수수료 요약</SummaryTitle>

          <SummaryLine>
            <span className="label">
              연간 총 운임
            </span>
            <span className="value">{formatKRW(annual.totalFare)}</span>
          </SummaryLine>

          <SummaryLine>
            <span className="label">
              연간 계속가맹금
              <span className="label-sub">(가맹 서비스 이용료)</span>
            </span>
            <span className="value">{formatKRW(annual.fee)}</span>
          </SummaryLine>

          <SummaryLine>
            <span className="label">
              연간 부가서비스
              <span className="label-sub">(기사님께 지급되는 추가 수익)</span>
            </span>
            <span className="value">{formatKRW(annual.service)}</span>
          </SummaryLine>

          <SummaryNote>
            {year}년 동안 헤이웨이를 통해 결제된 운임·수수료 내역을 기준으로 자동 집계된
            자료입니다.
            <br />
            부가세 및 종합소득세 신고 시 참고용으로만 활용해 주세요.
          </SummaryNote>
        </SummaryCard>


        {/* 에러 메시지 */}
        {err && <ErrorBox>{err}</ErrorBox>}

        {/* 월별 표 */}
        <TableOuter>
          <TableCard>
            <TableHead>
              <div className="col col-month">월</div>
              <div className="col col-total">월 총 운임</div>
              <div className="col col-fee">계속가맹금</div>
              <div className="col col-service">부가서비스</div>
            </TableHead>

            <TableBody>
              {monthRows.map((row) => (
                <TableRow key={row.month}>
                  <div className="col col-month">{row.month}월</div>
                  <div className="col col-total">
                    <div className="main">{formatKRW(row.totalFare)}</div>
                  </div>
                  <div className="col col-fee">
                    <div className="main">{formatKRW(row.fee)}</div>
                  </div>
                  <div className="col col-service">
                    <div className="main">{formatKRW(row.service)}</div>
                  </div>
                </TableRow>
              ))}

              <TableFooter>
                <div className="col col-month">합계</div>
                <div className="col col-total">
                  <div className="main">{formatKRW(annual.totalFare)}</div>
                </div>
                <div className="col col-fee">
                  <div className="main">{formatKRW(annual.fee)}</div>
                </div>
                <div className="col col-service">
                  <div className="main">{formatKRW(annual.service)}</div>
                </div>
              </TableFooter>
            </TableBody>

            {loading && <LoadingOverlay>불러오는 중...</LoadingOverlay>}
          </TableCard>

          <TableNote>
            {`• 본 자료는 헤이웨이 결제 매출 기준이며, 다른 플랫폼 매출은 포함되지 않습니다.
• 실제 신고 시에는 세무사·세무대리인의 안내에 따라 신고해 주세요.`}
          </TableNote>
        </TableOuter>
      </MainWrap>
    </BG>
  );
}

/* ===== 스타일 ===== */

const BG = styled.div`
  min-height: 100vh;

`;

const MainWrap = styled.div`
  padding: 0 16px 32px;
`;

const TitleBlock = styled.div`
  text-align: center;
  margin-bottom: 2px;
`;

const BrandRow = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
`;

const BrandKakao = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: #fbbf24;
`;

const BrandName = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #111827;
`;

const SubTitle = styled.div`
  margin-top: 6px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const YearRow = styled.div`
  margin: 18px auto 20px;
  max-width: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
`;

const YearArrow = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: none;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  font-size: 18px;
  display: grid;
  place-items: center;
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.35 : 1)};
`;

const YearText = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

/* 요약 카드 */

const SummaryCard = styled.section`
  background: #ffffff;
  border-radius: 24px;
  padding: 18px 18px 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  margin-bottom: 18px;
`;

const SummaryTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  margin-bottom: 6px;

  .label {
    color: #4b5563;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
  }

  .label-sub {
    font-size: 11px;
    color: #9ca3af;
  }

  .value {
    font-weight: 700;
    color: #111827;
  }
`;


const SummaryNote = styled.p`
  margin: 10px 0 0;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
`;

const ErrorBox = styled.div`
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
`;

/* 표 카드 전체 래퍼 (노트 포함) */

const TableOuter = styled.section`
  margin-top: 10px;
`;

/* 표 카드 */

const TableCard = styled.div`

  padding: 6px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  position: relative;
  overflow: hidden;
`;

/* 헤더 – radius 제거 */

const TableHead = styled.div`
  display: grid;
  grid-template-columns: 56px repeat(3, minmax(0, 1fr));
  column-gap: 8px;
  padding: 10px 14px 9px 12px;
  background: #f3f4ff;
  font-size: 11px;
  font-weight: 700;
  color: #4b5563;
  box-shadow: inset 0 -1px 0 rgba(148, 163, 184, 0.4);

  .col-month {
    text-align: left;
  }
  .col-total,
  .col-fee,
  .col-service {
    text-align: right;
  }
`;

/* 바디 */

const TableBody = styled.div`
  background: #ffffff;
`;

/* 일반 행 */

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 56px repeat(3, minmax(0, 1fr));
  column-gap: 8px;
  padding: 9px 14px 9px 12px;
  font-size: 12px;
  border-bottom: 1px solid #e5e7f5;

  &:nth-child(odd) {
    background: #f9fafc;
  }

  .col-month {
    font-weight: 600;
    color: #111827;
    text-align: left;
  }
  .col-total,
  .col-fee,
  .col-service {
    text-align: right;
  }
  .col-total .main,
  .col-fee .main,
  .col-service .main {
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
  }
`;

/* 합계 행 – radius 제거 */

const TableFooter = styled.div`
  display: grid;
  grid-template-columns: 56px repeat(3, minmax(0, 1fr));
  column-gap: 8px;
  padding: 10px 14px 12px 12px;
  font-size: 12px;
  background: linear-gradient(90deg, #e5edff, #ddeaff);

  .col-month {
    font-weight: 700;
    color: #111827;
    text-align: left;
  }
  .col-total,
  .col-fee,
  .col-service {
    text-align: right;
  }
  .col-total .main,
  .col-fee .main,
  .col-service .main {
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
  }
`;

const TableNote = styled.pre`
  margin: 8px 4px 0;
  padding: 8px 10px;
  border-radius: 14px;
  background: rgba(239, 246, 255, 0.8);
  white-space: pre-wrap;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.6);
  display: grid;
  place-items: center;
  font-size: 13px;
  color: #4b5563;
`;
